import type React from 'react'
import { Avatar, Popover } from 'antd'
import { UserOutlined } from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import dayjs from 'dayjs'

import { formatters } from '@utils/intl/dateTimeFormatters'
import BBDLogo from '../../../../../assets/Logos/bbd-logo.svg'

/**
 * Properties for UpdatedTag component
 */
export interface UpdatedTagProps {
  /**
   * Creation information
   */
  created: {
    /**
     * User who created the item
     */
    by: string
    /**
     * Creation date
     */
    date: string
  }
  /**
   * Update information
   */
  updated: {
    /**
     * User who last updated the item
     */
    by: string
    /**
     * Update date
     */
    date: string
  }
  /**
   * Title for the popover
   */
  title: string
}

const iconStyles = {
  width: 24,
  height: 24,
}

/**
 * Displays a tag showing when an item was created or updated
 * with a popover showing more details
 */
export const UpdatedTag: React.FC<UpdatedTagProps> = ({ created, updated, title }) => {
  const showModelGen = !created && !updated
  const hasBeenUpdated = !!updated
  const date = created.date ? created.date : hasBeenUpdated ? updated.date : created.date

  return (
    <Popover
      content={<UpdateDetails created={created} updated={updated} />}
      title={<UpdateTitle title={title} />}
      trigger='hover'
      placement='leftTop'
    >
      <div>
        <Horizontal className='order-stat-tag-container' style={{ flexWrap: 'nowrap', minWidth: 'fit-content' }}>
          {showModelGen ? (
            <Vertical
              verticalCenter
              borderRadius='6px 0 0 6px'
              flex='0'
              style={{
                overflow: 'visible',
                height: 20,
                background: 'var(--theme-color-2)',
              }}
            >
              <img className='p-1' src={BBDLogo} style={iconStyles} alt='Gravitate Logo' />
            </Vertical>
          ) : (
            <Vertical
              verticalCenter
              bg='info'
              border='all'
              borderRadius='6px 0 0 6px'
              flex='0'
              style={{ overflow: 'visible', height: 20 }}
            >
              <Texto category='label' appearance='secondary' weight='600' style={{ fontSize: '12px' }}>
                <UserOutlined className='px-2 py-2' />
              </Texto>
            </Vertical>
          )}

          <Vertical
            bg='elevated'
            border='all'
            borderRadius='0 6px 6px 0'
            flex={1}
            style={{ height: 20 }}
            className='px-2 vertical-flex-center h-100'
          >
            <Texto
              category='label'
              style={{
                fontWeight: hasBeenUpdated ? 'bold' : 'normal',
                flexWrap: 'nowrap',
              }}
            >
              {formatters.SHORT_DATE_TIME(dayjs(date))}
            </Texto>
          </Vertical>
        </Horizontal>
      </div>
    </Popover>
  )
}

/**
 * Properties for UpdateDetails component
 */
interface UpdateDetailsProps {
  /**
   * Creation information
   */
  created: {
    by: string
    date: string
  }
  /**
   * Update information
   */
  updated: {
    by: string
    date: string
  }
  /**
   * Whether to hide created info if null
   */
  ignoreCreatedIfNull?: boolean
}

/**
 * Component that displays the creation and update details
 */
export const UpdateDetails: React.FC<UpdateDetailsProps> = ({ created, updated, ignoreCreatedIfNull = false }) => {
  if (ignoreCreatedIfNull && !created.by) {
    return null
  }

  return (
    <div style={{ minWidth: 300 }}>
      <Horizontal>
        <Vertical flex={2} gap='4px'>
          <Texto category='label' weight='600' className='popover-subtitle'>
            Created By
          </Texto>
          <Horizontal className='popover-icon' alignItems='center'>
            {!created.by ? (
              <Avatar
                size={24}
                style={{
                  overflow: 'visible',
                  background: 'var(--theme-color-2)',
                }}
              >
                <img src={BBDLogo} alt='Gravitate Logo' />
              </Avatar>
            ) : (
              <Avatar
                size={24}
                style={{
                  overflow: 'visible',
                  background: 'var(--theme-color-2-dim)',
                  color: 'var(--theme-color-1)',
                  border: 'solid 1px var(--gray-300)',
                }}
              >
                <UserOutlined />
              </Avatar>
            )}
            <Texto category='p1' className='popover-status-text'>
              {created.by ? created.by : 'Gravitate'}
            </Texto>
          </Horizontal>
        </Vertical>
        <Vertical style={{ overflow: 'visible' }} gap='4px' flex={1} alignItems='flex-end'>
          <Texto className='popover-subtitle' category='label' weight='600'>
            At
          </Texto>
          <Texto style={{ whiteSpace: 'nowrap' }} category='p1' className='popover-status-text pl-2'>
            {created.date ? formatters.WEEKDAY_MONTH_DATE_TIME(dayjs(created.date)) : ''}
          </Texto>
        </Vertical>
      </Horizontal>

      {(updated.by || updated.date) && (
        <Horizontal>
          <Vertical gap='4px' flex={2} style={{ overflow: 'visible' }}>
            <Texto category='label' weight='600' className='popover-subtitle'>
              Last Updated By
            </Texto>
            <Horizontal className='popover-icon' alignItems='center'>
              <Avatar
                size={24}
                style={{
                  overflow: 'visible',
                  background: 'var(--theme-color-2-dim)',
                  color: 'var(--theme-color-1)',
                  border: 'solid 1px var(--gray-300)',
                }}
              >
                <UserOutlined />
              </Avatar>
              <Texto category='p1' className='popover-status-text'>
                {updated.by}
              </Texto>
            </Horizontal>
          </Vertical>

          <Vertical gap='4px' flex={1} alignItems='flex-end' style={{ overflow: 'visible' }}>
            <Texto category='label' className='popover-subtitle' weight='600'>
              At
            </Texto>
            <Texto style={{ whiteSpace: 'nowrap' }} category='p1' className='popover-status-text pl-2'>
              {updated.date ? formatters.WEEKDAY_MONTH_DATE_TIME(dayjs(updated.date)) : ''}
            </Texto>
          </Vertical>
        </Horizontal>
      )}
    </div>
  )
}

/**
 * Properties for UpdateTitle component
 */
interface UpdateTitleProps {
  title: string
}

/**
 * Component that displays the title for the update details popover
 */
const UpdateTitle: React.FC<UpdateTitleProps> = ({ title }) => {
  return (
    <Horizontal verticalCenter>
      <div style={{ flex: 1 }}>
        <div className='popover-title'>{title}</div>
      </div>
    </Horizontal>
  )
}
